import { Promise as BluebirdPromise } from 'bluebird';
import { isEmpty, keyBy } from 'lodash';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { RuntimeContext } from '../../../../common/RuntimeContext';
import { getManyCollections, getManyUserCollections, getManyUserLearningAssets } from '../../../shared/actions/index';
import VerbHeading from '../../../shared/components/VerbHeading';
import { makeGetUserCollectionByUser, makeGetUserCollectionContent } from '../../../shared/selectors/index';
import { getCompletionStatusForUserCollection } from '../../../shared/utils/UserLearningAssetUtils';
import GroupHeaderWidget from '../../components/widgets/CompositePageWidgets/Header/GroupHeaderWidget';
import ExploreCollectionsDisplayWidget from '../widgets/ExploreCollectionsDisplayWidget';

class DimensionPage extends PureComponent {
    static propTypes = {
        getManyCollections: PropTypes.func,
        getManyUserCollections: PropTypes.func,
        getManyUserLearningAssets: PropTypes.func,
        dimension: PropTypes.object,
        collections: PropTypes.array,
        userCollectionsByCollectionId: PropTypes.array,
        match: PropTypes.object,
        learningFramework: PropTypes.object,
        loggedInUserId: PropTypes.string,
        userCompany: PropTypes.object,
        userCompanyLicensedCollectionsById: PropTypes.object
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            collectionsLoading: true
        };
    }

    componentDidMount() {
        const { learningFramework, } = this.props;
        if (learningFramework && learningFramework._id) {
            return this._getDimensionInfo();
        }
        return null;
    }

    componentDidUpdate(prevProps) {
        const { learningFramework, } = this.props;
        if (
            (!prevProps.learningFramework || prevProps.learningFramework._id === undefined) &&
            // eslint-disable-next-line prettier/prettier
            learningFramework &&
            learningFramework._id
        ) {
            return this._getDimensionInfo();
        }
        return null;
    }

    _getDimensionInfo() {
        const {
            match: { params: { id } = {} } = {},
            getManyCollections,
            getManyUserCollections,
            getManyUserLearningAssets,
            loggedInUserId,
            learningFramework = {}
        } = this.props;

        this.setState({ loading: false });

        const promises = [
            getManyUserLearningAssets({ userProfileId: loggedInUserId, pathsContext: this.context.pathsWeb }),
            getManyUserCollections({ userProfileId: loggedInUserId, pathsContext: this.context.pathsWeb })
        ];
        if (learningFramework.isDefault) {
            promises.push(getManyCollections({ dimensionId: id, pathsContext: this.context.pathsWeb }));
        } else {
            const collectionIds = learningFramework.collectionsByDimensionId
                ? learningFramework.collectionsByDimensionId[id]
                : [];
            promises.push(getManyCollections({ _id: collectionIds, pathsContext: this.context.pathsWeb }));
        }

        return BluebirdPromise.all(promises).then(() => this.setState({ collectionsLoading: false }));
    }

    _getCompletedDate = (completedActivites) =>
        completedActivites.reduce((accum, activity) => {
            const completionDate = new Date(activity.completedDateTime);
            if (completionDate > accum) {
                return completionDate;
            }
            return accum;
        }, 0);

    // eslint-disable-next-line class-methods-use-this
    _arrangeCollections() {
        const { collections = [], userCollectionsByCollectionId = {} } = this.props;

        const { completed, notCompleted } = collections.reduce(
            (acc, collection) => {
                if (userCollectionsByCollectionId[collection._id]) {
                    const userCollection = userCollectionsByCollectionId[collection._id];
                    if (userCollection.status === 'COMPLETED') {
                        acc.completed.push({
                            ...collection,
                            completionDate: this._getCompletedDate(
                                userCollectionsByCollectionId[collection._id].content
                            )
                        });
                    } else {
                        acc.notCompleted.push(collection);
                    }
                } else {
                    acc.notCompleted.push(collection);
                }
                return acc;
            },
            {
                completed: [],
                notCompleted: []
            }
        );
        const completedCollectionsByCompletionDate = [...completed].sort((a, b) =>
            a.completionDate > b.completionDate ? 1 : -1
        );

        const orderedCollections = [...notCompleted, ...completedCollectionsByCompletionDate];
        return orderedCollections;
    }

    render() {
        const {
            dimension: { thumbnailImageUrl, name, description, _id: dimensionId } = {},
            userCollectionsByCollectionId = {},
            userCompany,
            userCompanyLicensedCollectionsById
        } = this.props;
        const { loading, collectionsLoading } = this.state;
        const orderedCollections = this._arrangeCollections();

        return (
            <div className={'dimension-page'}>
                <div className={'row'}>
                    <GroupHeaderWidget
                        company={userCompany}
                        className={'dimension-page-header'}
                        thumbnailImageUrl={thumbnailImageUrl}
                        title={name}
                        description={description}
                        loading={loading}
                        currentGroupId={dimensionId}
                    />
                </div>
                <div className={'container'}>
                    <div className={'row'}>
                        <VerbHeading hNumber={5} defaultMessage={'Collections'} messageId={'HEADING_COLLECTIONS'} />
                    </div>
                    <ExploreCollectionsDisplayWidget
                        userCollectionsByCollectionId={userCollectionsByCollectionId}
                        isLoading={collectionsLoading}
                        collections={orderedCollections}
                        userCompanyLicensedCollectionsById={userCompanyLicensedCollectionsById}
                        shouldSort={false}
                        pageLength={6}
                        cardClassName={'inline-collection-card col-12 col-sm-6'}
                    />
                </div>
            </div>
        );
    }
}

// maybe add selectors for the collection filters
export const makeMapStateToProps = () => {
    const getUserCollectionContent = makeGetUserCollectionContent();
    const getUserCollectionsObjectsFromStateById = makeGetUserCollectionByUser();
    const mapStateToProps = (state, ownProps) => {
        const {
            auth: { loggedInUser = {}, loggedInUserId } = {},
            collections: { byId: collectionsById = {} } = {},
            companies: { byId: companiesById = {} } = {},
            dimensions: { byId: dimensionsById = {} } = {},
            learningFrameworks: { byId: learningFrameworksById = {} } = {}
        } = state;
        const { match: { params = {} } = {} } = ownProps;
        const dimensionId = params.id;
        const dimension = dimensionsById[dimensionId];

        const userCompany = loggedInUser.companyId && companiesById[loggedInUser.companyId];

        let userCompanyLicensedCollectionsById;
        let collectionIds = [];
        const dimensionCollections = [];
        let learningFramework;
        if (!isEmpty(userCompany)) {
            userCompanyLicensedCollectionsById = keyBy(userCompany.collectionLicenses, 'collectionId');
            if (
                userCompany.hasCustomDimensions &&
                userCompany.frameworkId &&
                learningFrameworksById[userCompany.frameworkId]
            ) {
                learningFramework = learningFrameworksById[userCompany.frameworkId];
                collectionIds =
                    (learningFramework.collectionsByDimensionId &&
                        learningFramework.collectionsByDimensionId[dimensionId]) ||
                    [];

                collectionIds.forEach((id) => {
                    if (collectionsById[id]) {
                        dimensionCollections.push(collectionsById[id]);
                    }
                });
            } else {
                const frameworkIds = Object.keys(learningFrameworksById);
                for (let i = 0; i < frameworkIds.length; i += 1) {
                    if (!learningFramework && learningFrameworksById[frameworkIds[i]].isDefault) {
                        learningFramework = learningFrameworksById[frameworkIds[i]];
                    }
                }
                Object.values(collectionsById).forEach((collection) => {
                    if (collection.dimensionId === dimensionId) {
                        collectionIds.push(collection._id);
                        dimensionCollections.push(collection);
                    }
                });
            }
        }

        let userCollectionContent;
        let userCollectionCompletenessData;
        const userCollectionsArray = getUserCollectionsObjectsFromStateById(state, loggedInUser._id);
        const userCollectionsWithCompletenessDataByCollectionId = userCollectionsArray.reduce((acc, userCollection) => {
            userCollectionContent = getUserCollectionContent(userCollection, state);
            userCollectionCompletenessData = getCompletionStatusForUserCollection(userCollectionContent);
            if (userCollection.collectionId) {
                acc[userCollection.collectionId] = { ...userCollection, ...userCollectionCompletenessData };
            }
            return acc;
        }, {});

        return {
            dimension,
            collections: dimensionCollections,
            loggedInUserId,
            learningFramework,
            userCollectionsByCollectionId: userCollectionsWithCompletenessDataByCollectionId,
            userCompany,
            userCompanyLicensedCollectionsById
        };
    };
    return mapStateToProps;
};

export const mapDispatchToProps = (dispatch) => ({
    getManyCollections: (params) => dispatch(getManyCollections(params)),
    getManyUserCollections: (params) => dispatch(getManyUserCollections(params)),
    getManyUserLearningAssets: (params) => dispatch(getManyUserLearningAssets(params))
});

export const DimensionPageComponent = DimensionPage;
export default connect(makeMapStateToProps, mapDispatchToProps)(DimensionPage);
